import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function Dropdown({
  title,
  buttonClassName,
  dropdownClassName,
  orientation = 'bottom',
  children,
  closeTrigger, // when this value changes externally, force close
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  function toggleDropdown() {
    setIsOpen((prev) => !prev);
  }

  function handleClickOutside(event) {
    if (
      buttonRef.current &&
      !buttonRef.current.contains(event.target) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target)
    ) {
      setIsOpen(false);
    }
  }

  useEffect(() => {
    function updateDropdownPosition() {
      if (isOpen && buttonRef.current && dropdownRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const isTop = orientation === 'top';

        let left = buttonRect.left;
        let top = isTop
          ? buttonRect.top - dropdownRect.height - 10
          : buttonRect.bottom + 10;

        // Prevent right overflow
        if (left + dropdownRect.width > window.innerWidth) {
          left = buttonRect.right - dropdownRect.width;
        }

        // Prevent left overflow
        if (left < 0) {
          left = 10;
        }

        // Prevent bottom overflow (flip if needed)
        if (!isTop && top + dropdownRect.height > window.innerHeight) {
          top = buttonRect.top - dropdownRect.height - 10;
        }

        setDropdownPosition({
          top,
          left,
          width: Math.max(buttonRect.width, dropdownRect.width),
        });
      }
    }

    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isOpen, orientation]);

  // Close dropdown whenever external closeTrigger changes
  // Close only when external trigger changes (not on every isOpen change)
  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeTrigger]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={buttonRef} className="relative inline-block text-left">
      <button
        type="button"
        className={`inline-flex justify-center w-full text-base font-medium bg-white ${buttonClassName}`}
        id="options-menu"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
      >
        {typeof title === 'string' ? <span>{title}</span> : title}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`fixed z-[9999] p-2 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${dropdownClassName}`}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              minWidth: dropdownPosition.width,
              visibility: dropdownPosition.width ? 'visible' : 'hidden', // hidden until positioned
            }}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
          >
            <div className="py-1" role="none">
              {children}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

Dropdown.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  children: PropTypes.node.isRequired,
  buttonClassName: PropTypes.string,
  orientation: PropTypes.string,
  dropdownClassName: PropTypes.string,
  closeTrigger: PropTypes.any,
};

export default Dropdown;
